import './FantasyDraftHelper.css'
import rawPlayers from './fantasyFootball_docs/2026_fantasyPlayers.json'
import { useEffect, useState } from "react";
import {pickPlayer, calculatePlayerValues, undoLastPick} from './DraftFunctions/InitialDataFunction.js'

const players = rawPlayers.map((player, index) => ({
    ...player,
    id: index+1, //auto assign ID
    valueScore: null,
    Team: null,
    picknum: null,
    recommend: null,
    FptsBonus:null,
    DraftScoreBonus:null,
    comparisonFpts: null,
    gamesExpectedInStartinLineup: null,
    originalFPts: player.FPts,
    overPerformancePoints: null
}));

const FantasyDraftHelper = (props) => {
    //player management
    const [playerList, setPlayerList] = useState(players);
    const [filteredAndSortedPlayers, setFilteredAndSortedPlayers] = useState(playerList)
    const [projectedTeamFPts_all, setProjectedTeamFPts_all] = useState({})
    const [teamTopQBs, setTeamTopQBs] = useState([])
    const [teamTopRBs, setTeamTopRBs] = useState([])
    const [teamTopWRs, setTeamTopWRs] = useState([])
    const [teamTopTEs, setTeamTopTEs] = useState([])
    const [teamTopFLEXs, setTeamTopFLEXs] = useState([])
    const [teamTopKs, setTeamTopKs] = useState([])
    const [teamTopDEFs, setTeamTopDEFs] = useState([])
    const [teamTopBench, setTeamTopBench] = useState([])

    //filter management -------------------------------------------------------
    const sortOptions = ["ALL","RB", "WR", "TE", "QB", "K", "DEF", "FLEX"]

    const [positionFilter, setPositionFilter] = useState("ALL")
    const [draftStatusFilter, setDraftStatusFilter] = useState("AVAILABLE")
    const [teamSelection, setTeamSelection] = useState(1)
    const [playerSearch, setPlayerSearch] = useState('')
    const [showRawVS, setShowRawVS] = useState(false);
    const [checkResetIfSure, setCheckResetIfSure] = useState(false);

    //Draft Settings Management ------------------------------------------------
    const [numberOfTeams, setNumberOfTeams] = useState(10)
    const [draftStructure, setDraftStructure] = useState("snake")
    const [numberOfQBs, setNumberOfQBs] = useState(1)
    const [numberOfRBs, setNumberOfRBs] = useState(2)
    const [numberOfWRs, setNumberOfWRs] = useState(2)
    const [numberOfTEs, setNumberOfTEs] = useState(1)
    const [numberOfFLEXs, setNumberOfFLEXs] = useState(2)
    const [numberOfKs, setNumberOfKs] = useState(1)
    const [numberOfDEFs, setNumberOfDEFs] = useState(1)
    const [numberOfBenchs, setNumberOfBenchs] = useState(5)
    const [totalTeamSize, setTotalTeamSize] = useState()

    useEffect(()=>{ //calculate total team size
        setTotalTeamSize(numberOfQBs+numberOfRBs+numberOfWRs+numberOfTEs+numberOfFLEXs+numberOfKs+numberOfDEFs+numberOfBenchs)
    },[numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs])

    //Current Draft Pick Trackers -------------------------------------------------
    const [currentPick, setCurrentPick] = useState(1);
    const [isResetting, setIsResetting] =useState(false);
    const calculateRoundNumber = (pick, numberOfTeams) => Math.ceil(pick / numberOfTeams);
    const calculateTeamPick = (pick, numberOfTeams, draftStructure) => {
        const round = Math.ceil(pick / numberOfTeams);

        if (draftStructure === "linear") {
            return ((pick - 1) % numberOfTeams) + 1;
        } else {
            return round % 2 === 0
            ? numberOfTeams - ((pick - 1) % numberOfTeams)
            : ((pick - 1) % numberOfTeams) + 1;
        }
    };

    // adjust currentPick functions
    const handleNextPick = () => {
        setCurrentPick(prev => prev + 1);
        setPlayerSearch('');
        };
    const handlePrevPick = () => {
        if (currentPick > 1) {
        setCurrentPick(prev => prev - 1);
        setPlayerSearch('');
        }
    };

    // Reworking the displayed Players based on Filters:
    const normalizeString = (str) =>
        str
            .toLowerCase()
            .normalize('NFD')               // Normalize accents (e.g. é -> e + ´)
            .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
            .replace(/[^a-z0-9]/g, '');      // Remove everything except alphanumeric (also removes spaces)

    useEffect(()=>{ //update the filteredAndSortedPlayers variable based on the current filters
        const tempPlayerList = playerList
            .filter(player => {
            const positionMatch =
                positionFilter === 'ALL' ||
                (positionFilter === 'FLEX' && ['RB', 'WR', 'TE'].includes(player.POS)) ||
                player.POS === positionFilter;

            const statusMatch =
                draftStatusFilter === 'ALL' ||
                (draftStatusFilter === 'AVAILABLE' && player.Team === null) ||
                (draftStatusFilter === 'DRAFTED' && player.Team !== null);

            const searchTrimmed = playerSearch.trim();
            const searchMatch =
                searchTrimmed === '' ||
                normalizeString(player.Player).includes(normalizeString(searchTrimmed));

                return positionMatch && statusMatch && searchMatch;
            })
            .sort((a, b) => {
                // First sort by valueScore (descending), fallback to FPts if null
                const aScore = a.recommend ?? -Infinity;
                const bScore = b.recommend ?? -Infinity;
                if (bScore !== aScore) return bScore - aScore;

                // Then sort by ADP (descending)
                return a.ADP - b.ADP;
            });
            setFilteredAndSortedPlayers(tempPlayerList);
    },[playerList, positionFilter, draftStatusFilter, playerSearch])

    useEffect(()=>{ //create the filtered lists for the team display
        const topQBs = playerList.filter(p => p.Team === teamSelection && p.POS === 'QB').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfQBs)
        const topRBs = playerList.filter(p => p.Team === teamSelection && p.POS === 'RB').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfRBs)
        const topWRs = playerList.filter(p => p.Team === teamSelection && p.POS === 'WR').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfWRs)
        const topTEs = playerList.filter(p => p.Team === teamSelection && p.POS === 'TE').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfTEs)
        const topKs = playerList.filter(p => p.Team === teamSelection && p.POS === 'K').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfKs)
        const topDEFs = playerList.filter(p => p.Team === teamSelection && p.POS === 'DEF').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfDEFs)

        // Get IDs of already shown players
        const shownPlayerIDs = new Set([
        ...topQBs,
        ...topRBs,
        ...topWRs,
        ...topTEs,
        ].map(p => p.id));

        // Get FLEX players (not already shown)
        const topFLEXs = playerList
        .filter(p =>
            p.Team === teamSelection &&
            ['RB', 'WR', 'TE'].includes(p.POS) &&
            !shownPlayerIDs.has(p.id)
        )
        .sort((a, b) => b.FPts - a.FPts)
        .slice(0, numberOfFLEXs);

        // Get Banch players (not already shown)
        const shownAllPlayerIDs = new Set([
        ...topQBs,
        ...topRBs,
        ...topWRs,
        ...topTEs,
        ...topFLEXs,
        ...topKs,
        ...topDEFs,
        ].map(p => p.id));

        const benchPlayers = playerList
        .filter(p =>
            p.Team === teamSelection &&
            !shownAllPlayerIDs.has(p.id)
        )
        .sort((a, b) => b.FPts - a.FPts); // Optional: sort bench players too

        setTeamTopQBs(topQBs)
        setTeamTopRBs(topRBs)
        setTeamTopWRs(topWRs)
        setTeamTopTEs(topTEs)
        setTeamTopFLEXs(topFLEXs)
        setTeamTopKs(topKs)
        setTeamTopDEFs(topDEFs)
        setTeamTopBench(benchPlayers)

    },[teamSelection, playerList, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs])

    useEffect(()=>{
        const teamFPts = {};

        for (let team = 1; team <= numberOfTeams; team++) {
            const teamPlayers = playerList.filter(p => p.Team === team);

            const topQBs = teamPlayers.filter(p => p.POS === 'QB').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfQBs);
            const topRBs = teamPlayers.filter(p => p.POS === 'RB').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfRBs);
            const topWRs = teamPlayers.filter(p => p.POS === 'WR').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfWRs);
            const topTEs = teamPlayers.filter(p => p.POS === 'TE').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfTEs);
            const topKs = teamPlayers.filter(p => p.POS === 'K').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfKs);
            const topDEFs = teamPlayers.filter(p => p.POS === 'DEF').sort((a, b) => b.FPts - a.FPts).slice(0, numberOfDEFs);

            const shownPlayerIDs = new Set([
            ...topQBs,
            ...topRBs,
            ...topWRs,
            ...topTEs,
            ].map(p => p.id));

            const topFLEXs = teamPlayers
            .filter(p =>
                ['RB', 'WR', 'TE'].includes(p.POS) &&
                !shownPlayerIDs.has(p.id)
            )
            .sort((a, b) => b.FPts - a.FPts)
            .slice(0, numberOfFLEXs);

            const allTopPlayers = [...topQBs, ...topRBs, ...topWRs, ...topTEs, ...topFLEXs, ...topKs, ...topDEFs];
            const totalFPts = allTopPlayers.reduce((sum, p) => sum + (parseFloat(p.FPts) || 0), 0);

            teamFPts[team] = {
            teamNumber: team,
            totalFPts: totalFPts.toFixed(2),
            players: allTopPlayers,
            };
        }

        setProjectedTeamFPts_all(teamFPts);
    },[playerList, numberOfTeams, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfKs, numberOfDEFs, numberOfFLEXs])

    // PLAYER SELECTION FUNCTIONS
    const playerPickSubmission = (player) => {
        const tempPlayerList = pickPlayer(playerList, player.id, currentPick, calculateTeamPick(currentPick, numberOfTeams, draftStructure), calculateRoundNumber(currentPick, numberOfTeams));
        const RecalculatedPick = currentPick+1;
        const RecalculatedRoundNumber = Math.ceil(RecalculatedPick / numberOfTeams);
        let teamIndex;
        if (draftStructure === "linear") {
            // Always go in order
            teamIndex = ((RecalculatedPick-1) % numberOfTeams) + 1
        } else if (draftStructure === "snake") {
            // Reverse order every other round
            teamIndex = RecalculatedRoundNumber % 2 === 0 ? numberOfTeams - ((RecalculatedPick-1) % numberOfTeams) : ((RecalculatedPick-1) % numberOfTeams) + 1;
        } 
        const RecalculatedCurrentTeamPick = teamIndex;
        setPlayerList(calculatePlayerValues(tempPlayerList, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs))
        handleNextPick();
    }
    const undoPickPlayer = () => {
        const tempPlayerList = undoLastPick(playerList, currentPick, calculateTeamPick(currentPick, numberOfTeams, draftStructure), calculateRoundNumber(currentPick, numberOfTeams));
        let RecalculatedPick;
        if (currentPick > 1) {
            RecalculatedPick = currentPick-1;
        } else {
            RecalculatedPick = currentPick;
        }
        const RecalculatedRoundNumber = Math.ceil(RecalculatedPick / numberOfTeams);
        let teamIndex;
        if (draftStructure === "linear") {
            // Always go in order
            teamIndex = ((RecalculatedPick-1) % numberOfTeams) + 1
        } else if (draftStructure === "snake") {
            // Reverse order every other round
            teamIndex = RecalculatedRoundNumber % 2 === 0 ? numberOfTeams - ((RecalculatedPick-1) % numberOfTeams) : ((RecalculatedPick-1) % numberOfTeams) + 1;
        } 
        const RecalculatedCurrentTeamPick = teamIndex;
        setPlayerList(calculatePlayerValues(tempPlayerList, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs))
        handlePrevPick();
    }
    const settingChangeRecalculation = () =>{
        setPlayerList(calculatePlayerValues(playerList, currentPick, calculateTeamPick(currentPick, numberOfTeams, draftStructure), calculateRoundNumber(currentPick, numberOfTeams), numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs))
    }
    const resetDraft = () =>{
        if (checkResetIfSure){
            setIsResetting(true)
            setCurrentPick(1)
            setCheckResetIfSure(false)
        } else {
        // First click - ask for confirmation
        setCheckResetIfSure(true);
    }
    }
    useEffect(()=>{
        if (isResetting && currentPick===1){
            setPlayerList(calculatePlayerValues(players, currentPick, calculateTeamPick(currentPick, numberOfTeams, draftStructure), calculateRoundNumber(currentPick, numberOfTeams), numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs))
            setIsResetting(false)
        }
    },[isResetting,currentPick])

    useEffect(()=>{ //recalculate draft power if settings change
        settingChangeRecalculation()
    },[numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs])








    return ( 
        <div className="fantasyDraft">
            <div className="fantasyDraft-top">
                <div className="heading" style={{ fontSize: 'clamp(24px, 2vw, 48px)' }}><span className="stadi">Draft</span><span className="verse">-Optimizer</span></div>
            </div>

            {/* DRAFT SETTINGS BAR */}
            <div className="settings-bar">
                <div className="settings-bar-inner">
                    <label style={{ maxWidth: '60px', wordBreak: 'break-word' }}>DRAFT <br />SETTINGS</label>
                    <label>Draft Order:
                        <select value={draftStructure} onChange={(e) => setDraftStructure(e.target.value)}>
                            <option value="linear">Linear</option>
                            <option value="snake">Snake</option>
                        </select>
                    </label>
                    <label>League Size: <input type="number" min="1" value={numberOfTeams} onChange={e => setNumberOfTeams(+e.target.value)}/></label>
                    <label>QBs: <input type="number" min="0" value={numberOfQBs} onChange={e => setNumberOfQBs(+e.target.value)}/></label>
                    <label>RBs: <input type="number" min="0" value={numberOfRBs} onChange={e => setNumberOfRBs(+e.target.value)}/></label>
                    <label>WRs: <input type="number" min="0" value={numberOfWRs} onChange={e => setNumberOfWRs(+e.target.value)}/></label>
                    <label>TEs: <input type="number" min="0" value={numberOfTEs} onChange={e => setNumberOfTEs(+e.target.value)}/></label>
                    <label>FLEXs: <input type="number" min="0" value={numberOfFLEXs} onChange={e => setNumberOfFLEXs(+e.target.value)}/></label>
                    <label>K: <input type="number" min="0" value={numberOfKs} onChange={e => setNumberOfKs(+e.target.value)}/></label>
                    <label>DEF: <input type="number" min="0" value={numberOfDEFs} onChange={e => setNumberOfDEFs(+e.target.value)}/></label>
                    <label>Bench: <input type="number" min="0" value={numberOfBenchs} onChange={e => setNumberOfBenchs(+e.target.value)}/></label>
                </div>
            </div>

            {/* DRAFT STATUS BAR & FILTERS BAR*/}
            <div className="status-bar">
                <div className="status-bar-inner" style={{ backgroundColor: 'var(--bg-accent)' }}>
                    <label style={{ maxWidth: '60px', wordBreak: 'break-word' }}>VIEW<br />FILTERS</label>
                    <label>Sort to Draft Status:
                        <select value={draftStatusFilter} onChange={(e) => setDraftStatusFilter(e.target.value)}>
                            <option value="AVAILABLE">AVAILABLE</option>
                            <option value="DRAFTED">DRAFTED</option>
                            <option value="ALL">ALL</option>
                        </select>
                    </label>
                    <label>Sort to Position:
                        <select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
                        {sortOptions.map((opt) => (
                            <option key={opt} value={opt}>
                            {opt}
                            </option>
                        ))}
                        </select>
                    </label>
                    <label>Team Roster:
                        <select value={teamSelection} onChange={(e) => setTeamSelection(Number(e.target.value))}>
                        {[...Array(numberOfTeams)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                            {i + 1}
                            </option>
                        ))}
                        </select>
                    </label>
                </div>
                <div className="status-bar-inner" style={{ backgroundColor: 'var(--sub1)' }}>
                    <label style={{ maxWidth: '60px', wordBreak: 'break-word' }}>DRAFT <br />STATUS</label>
                    <label>Current Pick:<div className="status-readout">{currentPick}</div></label>
                    <label>Round:<div className="status-readout">{calculateRoundNumber(currentPick, numberOfTeams)}</div></label>
                    <label>Team Picking:<div className="status-readout">{calculateTeamPick(currentPick, numberOfTeams, draftStructure)}</div></label>
                </div>
            </div>
            
            {/* PLAYER TABLES */}
            <div className="player-displays">
                {/* PLAYERS TABLE */}
                <div className="player-displays-inner" style={{ padding: '10px' }}>
                    <div style={{backgroundColor: 'inherit',display: 'flex',justifyContent: 'space-between',alignItems: 'center',width: '100%', flexWrap: 'wrap',rowGap: '8px'}}>
                        {/* Left Side: Reset Draft */}
                        <button
                            onClick={resetDraft} // replace with your reset function
                            style={{
                            fontSize: 'clamp(8px, 1vw, 14px)',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            backgroundColor: 'var(--sub4)',
                            color: 'var(--bg)',
                            border: '2px solid black',
                            borderRadius: '4px',
                            }}>
                            {checkResetIfSure ? 'Are You Sure?': 'Reset Draft'}
                        </button>
                        {/* Right Side: Search + Undo */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap',rowGap: '8px'}}>
                            <label htmlFor="playerSearch" style={{ marginRight: '8px', backgroundColor: 'inherit' }}>
                            </label>
                            <input
                            id="playerSearch"
                            type="text"
                            value={playerSearch}
                            onChange={(e) => setPlayerSearch(e.target.value)}
                            style={{
                                flex: '1 1 10px',
                                maxWidth: '200px',
                                padding: '4px',
                                fontSize: 'clamp(10px, 1.2vw, 16px)',
                            }}
                            placeholder="Search players..."
                            />
                            <button
                            onClick={undoPickPlayer}
                            style={{
                                marginLeft: '4px',
                                fontSize: 'clamp(8px, 1vw, 14px)',
                                padding: '4px 4px',
                                cursor: 'pointer',
                                backgroundColor: 'var(--btn-bg, #eee)',
                                border: '2px solid var(--btn-border, #ccc)',
                                borderRadius: '4px',
                            }}
                            >
                            Undo Pick
                            </button>
                        </div>
                    </div>

                    {/* 🔽 Scrollable Table Container */}
                    <div className="scrollable-table-container">
                        <table className="player-table">
                            <thead>
                                <tr>
                                <th style={{borderBottom: 'none'}}></th>
                                <th>Player</th>
                                <th>POS</th>
                                <th>ADP</th>
                                <th>FPts</th>
                                <th>Draft Strength</th>
                                {showRawVS && (
                                <>
                                <th>Raw VS</th>
                                <th>FptsBonus</th>
                                <th>DraftScoreBonus</th>
                                <th>Games Starting (exp)</th>
                                <th>comparisonFpts</th>
                                <th>OverperformancePts</th>
                                </>
                                )}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedPlayers.map((player) => (
                                <tr key={player.id}>
                                    <td style={{ paddingRight: '0px', borderBottom: 'none'}}>
                                        {player.Team == null && (
                                        <button
                                            style={{
                                            fontSize: 'clamp(8px, 1vw, 14px)',
                                            padding: '2px 2px',
                                            lineHeight: 1,
                                            cursor: 'pointer',
                                            }}
                                            onClick={() => playerPickSubmission(player)}
                                        >
                                            Pick
                                        </button>
                                        )}
                                    </td>
                                    <td style={{paddingLeft: 0,textAlign: 'left'}}>{player.Player}</td>
                                    <td>{player.POS}</td>
                                    <td>{player.ADP}</td>
                                    <td>{player.originalFPts}</td>
                                    <td>{player.recommend}</td>
                                    {showRawVS && (
                                    <>
                                    <td>{player.valueScore ?? '-'}</td>
                                    <td>{player.FptsBonus}</td>
                                    <td>{player.DraftScoreBonus}</td>
                                    <td>{player.gamesExpectedInStartinLineup}</td>
                                    <td>{player.comparisonFpts}</td>
                                    <td>{player.overPerformancePoints}</td>
                                    </>
                                    )}
                                </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* TEAM PLAYERS VIEW */}
                <div className="player-displays-inner" style={{ padding: '10px' }}>
                    <label style={{ width: '100%', marginBottom: '8px' }}>Team {teamSelection} Roster</label>
                    <div className="scrollable-table-container">
                        <table className="player-table">
                        <thead>
                            <tr>
                            <th style={{ borderBottom: 'none' }}></th>
                            <th>Player</th>
                            <th>POS</th>
                            <th>ADP</th>
                            <th>FPts</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* 🔹 QB */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>QB</td></tr>
                            {teamTopQBs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 RB */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>RB</td></tr>
                            {teamTopRBs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 WR */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>WR</td></tr>
                            {teamTopWRs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 TE */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>TE</td></tr>
                            {teamTopTEs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 FLEX */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>FLEX (RB/WR/TE)</td></tr>
                            {teamTopFLEXs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 K */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>K</td></tr>
                            {teamTopKs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 DEF */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>DEF</td></tr>
                            {teamTopDEFs.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}

                            {/* 🔹 BENCH */}
                            <tr><td colSpan={6} style={{ borderBottom:'none', fontWeight: 'bold', padding: '4px 0' }}>Bench</td></tr>
                            {teamTopBench.map(player => (
                                <tr key={player.id}>
                                <td style={{ paddingRight: '0px', borderBottom: 'none' }}></td>
                                <td style={{ paddingLeft: 0, textAlign: 'left' }}>{player.Player}</td>
                                <td>{player.POS}</td>
                                <td>{player.ADP}</td>
                                <td>{player.FPts}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="team-projections-container">
                {Object.entries(projectedTeamFPts_all).map(([teamNumber, teamData]) => (
                    <div key={teamNumber} className="team-card">
                    <h3>Team {teamNumber}</h3>
                    <p><strong>Projected Year FPTS:</strong> {teamData.totalFPts}</p>
                    <ul>
                        {teamData.players.map(player => (
                        <li key={player.id}>
                            {player.Name} ({player.POS}) - {player.FPts} FPts
                        </li>
                        ))}
                    </ul>
                    </div>
                ))}
            </div>
            <button
            onClick={() => setShowRawVS(prev => !prev)}
            style={{
                marginLeft: '8px',
                fontSize: 'clamp(8px, 1vw, 14px)',
                padding: '4px 8px',
                cursor: 'pointer',
                backgroundColor: 'var(--btn-bg, #eee)',
                color: 'var(--btn-text, #000)',
                border: '2px solid var(--btn-border, #ccc)',
                borderRadius: '4px',
            }}
            >
            {showRawVS ? 'Hide Raw VS' : 'Show Raw VS'}
            </button>
        </div>
     );
}
 
export default FantasyDraftHelper;